import {
  createUrlSyncedTablePaginationController,
  TablePaginationState,
  UrlSyncedTablePaginationController,
} from "./table-pagination-url";

type NavigableApp = {
  navigate: (path: string) => void;
};

type DynamicTableConfig<TItem> = {
  app?: NavigableApp;
  paginationPrefix: string;
  persistedPagination?: Partial<TablePaginationState>;
  onPatchPagination: (pagination: TablePaginationState) => void;
  items: TItem[];
  filterFn?: (item: TItem) => boolean;
  searchable?: {
    query?: string;
    getSearchableText: (item: TItem) => string;
  };
};

export type DynamicTableState<TItem> = {
  filteredItems: TItem[];
  visibleItems: TItem[];
  paginationController?: UrlSyncedTablePaginationController;
};

const applySearch = <TItem>(
  items: TItem[],
  searchable: DynamicTableConfig<TItem>["searchable"]
): TItem[] => {
  if (!searchable?.query || !searchable.getSearchableText) {
    return items;
  }

  const normalizedQuery = searchable.query.trim().toLowerCase();
  if (!normalizedQuery) {
    return items;
  }

  return items.filter((item) =>
    searchable.getSearchableText(item).toLowerCase().includes(normalizedQuery)
  );
};

export const createDynamicTableState = <TItem>(
  config: DynamicTableConfig<TItem>
): DynamicTableState<TItem> => {
  const filteredItems = applySearch(
    config.filterFn ? config.items.filter(config.filterFn) : config.items,
    config.searchable
  );

  const paginationController = config.app
    ? createUrlSyncedTablePaginationController({
        app: config.app,
        prefix: config.paginationPrefix,
        totalItems: filteredItems.length,
        persistedPagination: config.persistedPagination,
        onPatchPagination: config.onPatchPagination,
      })
    : undefined;

  return {
    filteredItems,
    visibleItems: paginationController
      ? paginationController.paginateItems(filteredItems)
      : filteredItems,
    paginationController,
  };
};
